import { create } from 'zustand';
import type {
  CommandAck,
  RobotEvent,
  RobotState,
  RobotSummary,
} from '@fabricos/protocol-ts';
import { aliasFor, useAssetAliases } from '@/store/assetAliases';

/** Overlay the operator's alias (if any) onto the hub-registered name. */
function withAlias(r: RobotSummary): RobotSummary {
  const alias = aliasFor(r.id);
  return alias ? { ...r, name: alias } : r;
}

const TRAIL_MAX_POINTS = 600; // ~ 2 minutes at 5 Hz

export interface TrailPoint {
  lat: number;
  lon: number;
  ts_ms: bigint;
}

interface FleetStore {
  /** Roster of registered robots, keyed by id. */
  robots: Record<string, RobotSummary>;
  /** Latest telemetry snapshot, keyed by robot id. */
  states: Record<string, RobotState>;
  /** Position trail per robot. */
  trails: Record<string, TrailPoint[]>;
  /** Recent events (newest first), trimmed to N. */
  events: RobotEvent[];
  /** Recent acks (newest first), trimmed to N — used for toast feedback. */
  acks: CommandAck[];

  setRobots: (rs: RobotSummary[]) => void;
  addRobot: (r: RobotSummary) => void;
  removeRobot: (id: string) => void;
  applyState: (s: RobotState) => void;
  /** Apply a whole batch in ONE store notify. The WS layer coalesces the
   *  ~18 frames/s/drone firehose to ≤10 flushes/s — otherwise every frame
   *  re-renders every subscribed component and the UI lags behind. */
  applyStates: (batch: RobotState[]) => void;
  /** Prepend persisted trail history (boot restore from IndexedDB). Live
   *  points recorded since page-load stay at the tail. */
  hydrateTrails: (saved: Record<string, TrailPoint[]>) => void;
  pushEvent: (e: RobotEvent) => void;
  pushAck: (a: CommandAck) => void;

  clear: () => void;
}

const EVENT_MAX = 200;
const ACK_MAX = 100;

export const useFleet = create<FleetStore>((set) => ({
  robots: {},
  states: {},
  trails: {},
  events: [],
  acks: [],

  /** Operator alias overlays the hub-registered name at the roster entry
   *  points — every name consumer downstream gets it for free. */
  setRobots: (rs) =>
    set(() => ({
      robots: Object.fromEntries(rs.map((r) => [r.id, withAlias(r)])),
    })),

  addRobot: (r) =>
    set((s) => ({
      robots: { ...s.robots, [r.id]: withAlias(r) },
    })),

  removeRobot: (id) =>
    set((s) => {
      const robots = { ...s.robots };
      const states = { ...s.states };
      const trails = { ...s.trails };
      delete robots[id];
      delete states[id];
      delete trails[id];
      return { robots, states, trails };
    }),

  applyState: (state) =>
    set((s) => {
      const next: Partial<FleetStore> = {
        states: { ...s.states, [state.robot_id]: state },
      };
      // Keep the roster connection status in sync.
      const existingRobot = s.robots[state.robot_id];
      if (existingRobot) {
        next.robots = {
          ...s.robots,
          [state.robot_id]: { ...existingRobot, connection: state.connection },
        };
      }
      // Append to trail if position is present and moved meaningfully.
      if (state.position) {
        const existing = s.trails[state.robot_id] ?? [];
        const last = existing[existing.length - 1];
        const moved =
          !last ||
          Math.abs(last.lat - state.position.lat) > 1e-6 ||
          Math.abs(last.lon - state.position.lon) > 1e-6;
        if (moved) {
          const pt: TrailPoint = {
            lat: state.position.lat,
            lon: state.position.lon,
            ts_ms: state.ts_ms,
          };
          const trail: TrailPoint[] = [...existing, pt].slice(-TRAIL_MAX_POINTS);
          next.trails = { ...s.trails, [state.robot_id]: trail };
        }
      }
      return next;
    }),

  applyStates: (batch) =>
    set((s) => {
      const states = { ...s.states };
      let robots = s.robots;
      let robotsCloned = false;
      let trails = s.trails;
      let trailsCloned = false;
      for (const state of batch) {
        states[state.robot_id] = state;
        const existingRobot = robots[state.robot_id];
        if (existingRobot && existingRobot.connection !== state.connection) {
          if (!robotsCloned) {
            robots = { ...robots };
            robotsCloned = true;
          }
          robots[state.robot_id] = { ...existingRobot, connection: state.connection };
        }
        if (state.position) {
          const existing = trails[state.robot_id] ?? [];
          const last = existing[existing.length - 1];
          const moved =
            !last ||
            Math.abs(last.lat - state.position.lat) > 1e-6 ||
            Math.abs(last.lon - state.position.lon) > 1e-6;
          if (moved) {
            if (!trailsCloned) {
              trails = { ...trails };
              trailsCloned = true;
            }
            const pt: TrailPoint = {
              lat: state.position.lat,
              lon: state.position.lon,
              ts_ms: state.ts_ms,
            };
            trails[state.robot_id] = [...existing, pt].slice(-TRAIL_MAX_POINTS);
          }
        }
      }
      return { states, robots, trails };
    }),

  hydrateTrails: (saved) =>
    set((s) => {
      const trails = { ...s.trails };
      for (const [robotId, pts] of Object.entries(saved)) {
        trails[robotId] = [...pts, ...(trails[robotId] ?? [])].slice(-TRAIL_MAX_POINTS);
      }
      return { trails };
    }),

  pushEvent: (e) =>
    set((s) => ({
      events: [e, ...s.events].slice(0, EVENT_MAX),
    })),

  pushAck: (a) =>
    set((s) => ({
      acks: [a, ...s.acks].slice(0, ACK_MAX),
    })),

  clear: () =>
    set({ robots: {}, states: {}, trails: {}, events: [], acks: [] }),
}));

// Alias edits re-apply to the live roster immediately (the overlay above
// only runs when rosters ARRIVE).
useAssetAliases.subscribe((s) => {
  useFleet.setState((fs) => ({
    robots: Object.fromEntries(
      Object.entries(fs.robots).map(([id, r]) => [
        id,
        { ...r, name: s.aliases[id] ?? r.name },
      ]),
    ),
  }));
});
